import { Component, computed, effect, inject, OnDestroy, signal, Signal, viewChild } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { LoadingSpinnerExtendedComponent, WizardComponent } from '@mwe/ui/components';
import { IWizardModel } from '@mwe/models';
import { ROUTE_HOME, ROUTE_METER_POINTS } from '../../app.constants';
import { NavigationStateService } from '@mwe/services';
import { SignalState } from '@shared-ikp/service/signal-state-service/signal-state.service';
import { signalStateFactory } from '@shared-ikp/service/signal-state-service/signal-state.utils';
import { MeterPointChangeSignalState, MeterPointChangeState } from './meterpoint-change-request.types';
import {
  closeMeterPointChangeRequestWizard,
  getMeterPointChangeRequestSteps,
  initialMeterPointChangeRequestState,
} from './meter-point-change-request.utils';
import { MeterPointService } from '../../service/meter-point/meter-point.service';
import { MeterPointChangeService } from '../../service/meter-point/meter-point-change.service';
import { MeterPoint } from '../../model/meter-point.model';
import { finalize, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { IkpPopupService } from '@shared-ikp/service/popup/popup.service';

@Component({
  selector: 'ikp-meter-point-change-request',
  templateUrl: './meter-point-change-request.component.html',
  standalone: true,
  imports: [RouterOutlet, WizardComponent, LoadingSpinnerExtendedComponent],
  providers: [
    {
      provide: SignalState,
      useFactory: () => signalStateFactory<MeterPointChangeState>(),
    },
  ],
})
export class MeterPointChangeRequestComponent implements OnDestroy {
  private readonly navigationStateService = inject(NavigationStateService);

  protected wizardStore: MeterPointChangeSignalState = inject(SignalState);
  protected meterPointService = inject(MeterPointService);
  private readonly wizardComponentRef = viewChild(WizardComponent);
  private readonly popupService = inject(IkpPopupService);
  private readonly router = inject(Router);
  private readonly ummeldungService = inject(MeterPointChangeService);
  meterPoint = signal<MeterPoint>(null);
  isLoading = signal<boolean>(false);

  protected wizardModel: Signal<IWizardModel> = computed(() => ({
    titleKey: 'meterPoint.meterPointChangeRequest.title',
    isDeepLinkEnabled: true,
    forwardDirectionChangeIx: 0,
    hideProgressBar: true,
    useMoreWidth: true,
    wizardSteps: getMeterPointChangeRequestSteps(this.wizardStore.state()),
    customCancelLogic: () => this.customCancel(),
  }));

  constructor() {
    if (this.router.currentNavigation().extras.state?.contractNumber && this.router.currentNavigation().extras.state?.meterPointId) {
      const contractNumber = this.router.currentNavigation().extras.state?.contractNumber;
      const meterPointId = this.router.currentNavigation().extras.state.meterPointId;
      this.loadMeterPoint(contractNumber, meterPointId);
    } else {
      this.meterPoint.set(this.meterPointService.meterPoints()[0]);
    }
    effect(() => {
      if (this.meterPoint()) {
        this.wizardStore.patchState({ meterPoint: this.meterPoint() });
      }
    });
    this.navigationStateService.wizardCancelUrl = `/${ROUTE_HOME}/${ROUTE_METER_POINTS}`;
    this.navigationStateService.wizardSuccessUrl = `/${ROUTE_HOME}/${ROUTE_METER_POINTS}`;
    this.wizardStore.initialize({
      initialState: initialMeterPointChangeRequestState,
      storageKey: 'meterPointChangeReqWizard',
    });
  }

  loadMeterPoint(contractNumber: string, meterPointId: string) {
    this.isLoading.set(true);
    this.meterPointService
      .loadMeterPoint(contractNumber, meterPointId)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: meterPoint => this.meterPoint.set(meterPoint as MeterPoint),
      });
  }

  customCancel(): void {
    const currentStepIdx = this.wizardComponentRef().currentWizardStepIdx;
    const contractNumber = this.wizardStore.state().meterPoint?.vertragsnummer;
    const meterPointNumber = this.wizardStore.state().meterPoint?.zaehlpunktnummer;

    const noWarningClose = currentStepIdx === 1 || currentStepIdx >= 6;

    if (noWarningClose) {
      closeMeterPointChangeRequestWizard(this.popupService, this.router, this.wizardStore, true, null);
      return;
    }

    this.ummeldungService
      .getMeterPointChangeRequest(contractNumber, meterPointNumber)
      .pipe(
        catchError(error => (error instanceof HttpErrorResponse && error.status === 404 ? of(null) : throwError(() => error))),
        map(changeRequest => changeRequest !== null),
      )
      .subscribe({
        next: documentFound => {
          const action = documentFound ? this.ummeldungService.deleteMeterpointChange(contractNumber, meterPointNumber) : null;
          closeMeterPointChangeRequestWizard(this.popupService, this.router, this.wizardStore, false, action);
        },
        error: () => this.ummeldungService.handleGenericErrorPopup('cancel-error'),
      });
  }

  ngOnDestroy(): void {
    this.wizardStore.reset();
  }
}
